import { Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import { useState } from "react";

import {
  ICustomMedication,
  ICustomMedicationName,
  ISaveCustomMedicationRequest,
} from "../../../@types/admin/AdminMedication";
import {
  useCreateCustomMedication,
  useUpdateCustomMedication,
} from "../../../hooks/mutations/admin/customMedications";
import {
  useLanguageOptions,
  useMedicationFormOptions,
} from "../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  RowAction,
  WarningNotice,
} from "../shared";
import {
  NameRow,
  NameRows,
  NotesInput,
  VisuallyHiddenLabel,
} from "./CustomMedications.styles";

interface ICustomMedicationModalProps {
  hospitalGroupId: number;
  /** Absent for a create. */
  medication?: ICustomMedication;
  onDone: () => void;
}

/**
 * Mirrors the API validator: names stay below the patient record's 512
 * characters, which stores the name followed by the strength and the form.
 */
const MAX_NAME_LENGTH = 400;
const MAX_STRENGTH_LENGTH = 64;
const MAX_NOTES_LENGTH = 1024;

/** A new item starts with an English name: the API's fallback language. */
const DEFAULT_LANGUAGE = "en-GB";

/** Blank optional texts go to the API as null, not as empty strings. */
function optional(value: string): string | null {
  return value.trim() || null;
}

/**
 * Creates or edits one of a hospital's custom medications (EN-5240).
 *
 * ⚠️ Patients see what is typed here in the app's medication search, and it
 * becomes part of their medication record. Only items from the list the care
 * team defined and confirmed may be added, which is why the dialog asks for the
 * sign-off reference in Notes.
 *
 * A custom medication is not an official dataset, so its name is a set of
 * translations — one row per language, as many as the care team provided —
 * rather than fixed Dutch/French fields. Patients see the one for their app
 * language, falling back to English, then to the first. The form is picked
 * from a fixed list whose labels are translated for them; the strength is free
 * text because numbers and units read the same in every language.
 */
export function CustomMedicationModal({
  hospitalGroupId,
  medication,
  onDone,
}: ICustomMedicationModalProps) {
  const isEdit = medication !== undefined;
  const { showToast } = useToast();
  const { languages } = useLanguageOptions();
  const { forms } = useMedicationFormOptions();
  const { createCustomMedication, isCreatingCustomMedication } =
    useCreateCustomMedication();
  const { updateCustomMedication, isUpdatingCustomMedication } =
    useUpdateCustomMedication();

  const [names, setNames] = useState<ICustomMedicationName[]>(() =>
    medication?.names?.length
      ? medication.names.map((name) => ({ ...name }))
      : [{ languageCode: DEFAULT_LANGUAGE, value: "" }]
  );
  const [strength, setStrength] = useState(medication?.strength ?? "");
  const [formCode, setFormCode] = useState(medication?.formCode ?? "");
  const [notes, setNotes] = useState(medication?.notes ?? "");
  const [touched, setTouched] = useState(false);

  const isSaving = isCreatingCustomMedication || isUpdatingCustomMedication;

  const usedLanguages = new Set(names.map((name) => name.languageCode));
  const unusedLanguages = languages.filter(
    (language) => !usedLanguages.has(language.id)
  );

  const rowErrors = names.map((name) => ({
    languageCode: name.languageCode ? undefined : "Choose a language.",
    value: name.value.trim() ? undefined : "Name is required.",
  }));
  const hasErrors = rowErrors.some((row) => row.languageCode || row.value);

  const updateName = (index: number, patch: Partial<ICustomMedicationName>) =>
    setNames((previous) =>
      previous.map((name, i) => (i === index ? { ...name, ...patch } : name))
    );

  const addLanguage = () =>
    setNames((previous) => [
      ...previous,
      { languageCode: unusedLanguages[0]?.id ?? "", value: "" },
    ]);

  const removeLanguage = (index: number) =>
    setNames((previous) => previous.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    setTouched(true);
    if (hasErrors) return;

    const request: ISaveCustomMedicationRequest = {
      names: names.map((name) => ({
        languageCode: name.languageCode,
        value: name.value.trim(),
      })),
      strength: optional(strength),
      formCode: formCode || null,
      notes: optional(notes),
    };

    try {
      if (isEdit) {
        await updateCustomMedication({
          hospitalGroupId,
          id: medication!.id,
          request,
        });
        showToast("The custom medication was updated.", "success");
      } else {
        await createCustomMedication({ hospitalGroupId, request });
        showToast("The custom medication was added.", "success");
      }
      onDone();
    } catch (error) {
      // The endpoint names a clash with another item's name (in any language,
      // including deactivated items) or an unknown language.
      showToast(
        errorToastMessage(
          error,
          "There was an error saving the custom medication."
        ),
        "warning"
      );
    }
  };

  const submitLabel = (() => {
    if (isSaving) return "Saving...";
    return isEdit ? "Save changes" : "Add medication";
  })();

  return (
    <ModalContainer>
      <ModalTitle>
        {isEdit ? "Edit custom medication" : "New custom medication"}
      </ModalTitle>
      <ModalForm>
        {!isEdit && (
          <ModalBodyText>
            Patients of this hospital will find it in the app&apos;s medication
            search, listed before the SAM or SNOMED results.
          </ModalBodyText>
        )}

        <WarningNotice>
          Only add medications from the list the care team defined and confirmed
          for this hospital.
        </WarningNotice>

        <FilterGroup>
          <FilterLabel>Name</FilterLabel>
          <NameRows>
            {names.map((name, index) => {
              const languageChoices = languages.filter(
                (language) =>
                  language.id === name.languageCode ||
                  !usedLanguages.has(language.id)
              );
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index}>
                  <VisuallyHiddenLabel
                    htmlFor={`custom-medication-language-${index}`}
                  >
                    Language {index + 1}
                  </VisuallyHiddenLabel>
                  <NameRow>
                    <AdminSelectField
                      id={`custom-medication-language-${index}`}
                      value={name.languageCode}
                      onChange={(e) =>
                        updateName(index, { languageCode: e.target.value })
                      }
                    >
                      <option value="">Language</option>
                      {languageChoices.map((language) => (
                        <option key={language.id} value={language.id}>
                          {language.name} ({language.id})
                        </option>
                      ))}
                    </AdminSelectField>
                    <FilterInput
                      aria-label={`Name ${index + 1}`}
                      type="text"
                      maxLength={MAX_NAME_LENGTH}
                      value={name.value}
                      onChange={(e) =>
                        updateName(index, { value: e.target.value })
                      }
                    />
                    {names.length > 1 ? (
                      <RowAction
                        label="Remove this language"
                        icon={<Cross2Icon />}
                        onClick={() => removeLanguage(index)}
                      />
                    ) : (
                      <span />
                    )}
                  </NameRow>
                  {touched && rowErrors[index].languageCode && (
                    <FieldError>{rowErrors[index].languageCode}</FieldError>
                  )}
                  {touched && rowErrors[index].value && (
                    <FieldError>{rowErrors[index].value}</FieldError>
                  )}
                </div>
              );
            })}
          </NameRows>
          <div>
            <FilterButton
              variant="secondary"
              disabled={unusedLanguages.length === 0}
              onClick={addLanguage}
            >
              <PlusIcon /> Add language
            </FilterButton>
          </div>
          <FieldHint>
            Patients see the name for their app language, then English, then the
            first one listed.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="custom-medication-strength">
            Strength (optional)
          </FilterLabel>
          <FilterInput
            id="custom-medication-strength"
            type="text"
            maxLength={MAX_STRENGTH_LENGTH}
            placeholder="e.g. 1000 IU"
            value={strength}
            onChange={(e) => setStrength(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="custom-medication-form">
            Form (optional)
          </FilterLabel>
          <AdminSelectField
            id="custom-medication-form"
            value={formCode}
            onChange={(e) => setFormCode(e.target.value)}
          >
            <option value="">No form</option>
            {forms.map((form) => (
              <option key={form.id} value={form.id}>
                {form.name}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>
            Shown to patients after the name and strength, translated to their
            app language.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="custom-medication-notes">Notes</FilterLabel>
          <NotesInput
            id="custom-medication-notes"
            rows={3}
            maxLength={MAX_NOTES_LENGTH}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <FieldHint>
            The care-team sign-off reference. Never shown to patients.
          </FieldHint>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving}
            onClick={handleSubmit}
          >
            {submitLabel}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
